import React from 'react';
import type { ProductDetail } from '@/api/models';
import { useUpdateProductMutation } from '@/api/queries';
import { showLocalizedToast } from '@/components/localizedToast';
import { useResetOnKeyChange } from '@/utils';
import {
    draftIssueItemsOf,
    publishRefusalOf,
    publishRequestOf,
    type DraftIssueItem,
    type DraftPublishRefusal,
} from './draftPanelMeta';

export type DraftPublisher = {
    /** The product is a draft, as the query has it. */
    isDraft: boolean;
    /** Why it is a draft, one sentence per `draftIssues` entry. */
    issues: DraftIssueItem[];
    isPublishing: boolean;
    /** The last publish's refusal, shown in the panel; `null` when none. */
    refusal: DraftPublishRefusal | null;
    onPublish: () => void;
};

type UseDraftPublishParams = {
    productId: string | null;
    product: ProductDetail | null;
};

/** The danger toast of a publish that failed outside the panel, `description` its reason. */
const showRefused = (description: string) => {
    showLocalizedToast(
        'danger',
        { key: 'products.detail.draft.refusedTitle' },
        { description: { key: description } },
    );
};

/**
 * A draft's panel on the product page (bos-app#225, US-18, AC-17).
 *
 * - **Publish** sends the product update as stored with `draft: false`
 *   (`publishRequestOf`). The mutation writes the reply's draft state into
 *   the cached detail, so the panel goes at once, before the refetch
 *   answers, and refreshes every list, so the list row stops being flagged;
 *   a success toast says the product is for sale. The flag is read from the
 *   product, never kept here (rulebook §11).
 * - **No category:** nothing is sent, since the update requires one; the
 *   panel asks the owner to choose one with Edit (`publishRefusalOf`).
 * - **A 400** stays in the panel, which names the variant still selling at
 *   0 when the product has one (`publishRefusalOf`). Another failure is a
 *   danger toast: a 404 says the product no longer exists, a 403 that the
 *   role may not publish, and anything else (no answer included) asks to try
 *   again. The panel and its Publish stay either way.
 * - **Saving lock:** a second Publish while one runs is ignored. An answer
 *   for a product the page no longer shows is dropped, and moving to another
 *   product clears the refusal.
 */
export const useDraftPublish = ({
    productId,
    product,
}: UseDraftPublishParams): DraftPublisher => {
    const mutation = useUpdateProductMutation();

    const [refusal, setRefusal] = React.useState<DraftPublishRefusal | null>(
        null,
    );
    const [isPublishing, setIsPublishing] = React.useState(false);

    const publishing = React.useRef(false);
    // The product on the page now, read after the request answers.
    const current = React.useRef(productId);

    useResetOnKeyChange(productId, () => {
        current.current = productId;
        setRefusal(null);
    });

    const issues = React.useMemo(
        () => draftIssueItemsOf(product?.draftIssues ?? []),
        [product],
    );

    const publish = React.useCallback(async () => {
        if (publishing.current || !productId || !product) {
            return;
        }
        const body = publishRequestOf(product);
        if (!body) {
            setRefusal(publishRefusalOf(product));
            return;
        }
        publishing.current = true;
        setIsPublishing(true);
        try {
            const result = await mutation.mutateAsync({
                id: productId,
                body,
            });
            if (current.current !== productId) {
                return;
            }
            if (result.success) {
                setRefusal(null);
                showLocalizedToast(
                    'success',
                    { key: 'products.detail.draft.published' },
                    {
                        description: {
                            key: 'products.detail.draft.publishedDescription',
                        },
                    },
                );
                return;
            }
            const { status } = result.error;
            if (status === 400) {
                setRefusal(publishRefusalOf(product));
                return;
            }
            // A retry cannot help a product that is gone or a role that may
            // not publish, so neither asks for one (review of bos-app#229,
            // finding 9).
            showRefused(
                status === 404
                    ? 'products.detail.draft.gone'
                    : status === 403
                      ? 'products.detail.draft.forbidden'
                      : 'products.detail.draft.noAnswer',
            );
        } catch {
            showRefused('products.detail.draft.noAnswer');
        } finally {
            publishing.current = false;
            setIsPublishing(false);
        }
    }, [mutation, product, productId]);

    const onPublish = React.useCallback(() => {
        void publish();
    }, [publish]);

    return {
        isDraft: product?.draft === true,
        issues,
        isPublishing,
        refusal,
        onPublish,
    };
};
